import type { Tutorial } from '@otter-ai/recall-client';
import * as recallApi from '@otter-ai/recall-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { tutorialsQueryCreator } from '@/query/tutorialsQueryCreator';
import type { TutorialName } from './types';

/**
 * Custom hook for managing tutorial data.
 * This hook provides functions to get, check, and update tutorial information.
 */
export const useTutorial = () => {
  const queryClient = useQueryClient();

  const { data: tutorialsResponse, isLoading: isTutorialsLoading } = useQuery(
    tutorialsQueryCreator(),
  );

  const tutorials = useMemo(() => {
    if (!tutorialsResponse?.tutorials) return [];
    return tutorialsResponse.tutorials;
  }, [tutorialsResponse]);

  /**
   * Retrieves a specific tutorial by its name.
   *
   * @param name - The name of the tutorial to retrieve.
   * @returns The requested tutorial if found, otherwise throws an error.
   */
  const getTutorial = useCallback(
    (name: string): Tutorial | undefined => {
      const tutorial = tutorials.find((tutorial) => tutorial.name === name);
      return tutorial;
    },
    [tutorials],
  );

  /**
   * Checks whether a specific tutorial is completed.
   *
   * @param name - The unique name of the tutorial to check.
   * @returns True if the tutorial is completed, false otherwise or if the tutorial doesn't exist.
   */
  const isTutorialCompleted = useCallback(
    (name: TutorialName): boolean | undefined => {
      const tutorial = getTutorial(name);
      if (!tutorial) return undefined;

      return tutorial.completed;
    },
    [getTutorial],
  );

  /**
   * Creates a mutation for updating tutorial data, allowing asynchronous API calls.
   * It will update the state after a successful mutation and handle errors appropriately.
   *
   * @param {Object} updatedTutorial - The updated values of the tutorial.
   * @param {TutorialName} updatedTutorial.name - The name of the tutorial to be updated.
   * @param {boolean} [updatedTutorial.canPrompt] - Deprecated in the new tutorial flow. It is retained for backward compatibility but is not actively used.
   * @param {number} [updatedTutorial.currentStep] - The current step number of the tutorial.
   * @param {boolean} [updatedTutorial.completed] - Indicates if the tutorial is completed.
   *
   * @returns {Promise<void>} - Returns a promise that resolves if the tutorial is updated successfully.
   *
   * @throws Will throw an error if the update request fails.
   */
  const { mutateAsync: updateTutorialMutation, isPending } = useMutation({
    mutationFn: async (updatedTutorial: {
      name: TutorialName;
      canPrompt?: boolean;
      currentStep?: number;
      completed?: boolean;
    }) => {
      const { name, canPrompt, currentStep, completed } = updatedTutorial;

      try {
        const response = await recallApi.updateTutorial({
          body: {
            name: name,
            appid: 'otter-web',
            current_step: currentStep,
            can_prompt: canPrompt,
            completed: completed,
          },
        });

        if (response.data) {
          return response.data;
        }
        throw new FailedToUpdateTutorialError(name);
      } catch (error) {
        console.error(`Error updating tutorial "${name}":`, error);
        throw new FailedToUpdateTutorialError(name);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tutorials'] });
    },
    onError: (error) => {
      console.error('Error updating tutorial:', error);
    },
  });

  /**
   * Updates a specific tutorial by its name.
   *
   * @param {TutorialName} name - The name of the tutorial to be updated.
   * @param {Object} updatedProperties - The properties of the tutorial to be updated.
   * @param {boolean} [updatedProperties.canPrompt] - Deprecated in the new tutorial flow. It is retained for backward compatibility but is not actively used.
   * @param {number} [updatedProperties.currentStep] - The current step number of the tutorial.
   * @param {boolean} [updatedProperties.completed] - Indicates if the tutorial is completed.
   *
   * @returns {Promise<void>} - A promise that resolves once the update is successful.
   *
   * @throws Will log an error if the tutorial is not found or the update fails.
   */
  const updateTutorial = useCallback(
    async (
      name: TutorialName,
      updatedProperties: {
        canPrompt?: boolean;
        currentStep?: number;
        completed?: boolean;
      },
    ) => {
      try {
        await updateTutorialMutation({ name, ...updatedProperties });
      } catch (error) {
        console.error('Failed to update tutorial:', error);
        throw new FailedToUpdateTutorialError(name);
      }
    },
    [updateTutorialMutation],
  );

  return {
    getTutorial,
    updateTutorial,
    tutorialIsUpdating: isPending,
    isTutorialCompleted,
    isTutorialsLoading,
  };
};

/**
 * Error thrown when a tutorial cannot be found by its name.
 */
export class TutorialNotFoundError extends Error {
  name = 'TutorialNotFoundError';
  constructor(public tutorialName: string) {
    super(`Tutorial with name "${tutorialName}" could not be found.`);
  }
}

/**
 * Error thrown when the update request for a tutorial fails.
 */
export class FailedToUpdateTutorialError extends Error {
  name = 'FailedToUpdateTutorialError';
  constructor(public tutorialName: string) {
    super(`Failed to update tutorial with name "${tutorialName}".`);
  }
}
